import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { filter } from 'rxjs';

import type { AdminSection } from '../analytics/analytics.events';
import { AnalyticsService } from '../analytics/analytics.service';
import {
  isDefaultAdminEntry,
  resolveAdminEntrySource,
  resolveAdminRefireSource,
  resolveAdminSection,
} from './admin-analytics.helpers';
import { AdminNavigationComponent } from './navigation.component';

@Component({
  selector: 'squadbox-admin',
  imports: [RouterOutlet, AdminNavigationComponent],
  template: `
    <div class="flex h-screen bg-gradient-e-vertical overflow-hidden">
      <squadbox-admin-navigation></squadbox-admin-navigation>
      <div class="flex-1 min-w-0 overflow-auto">
        <router-outlet></router-outlet>
      </div>
    </div>
  `,
})
export class AdminPage {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly analyticsService = inject(AnalyticsService);

  /**
   * Section the last event reported. Guards the refire against navigations that
   * do not change section (query params, a relink to the current section) — the
   * event answers "a section became active", not "the URL moved". `undefined`
   * until the first event, which is what identifies that one as the entry.
   */
  private reportedSection: AdminSection | undefined;

  /**
   * How the navigation currently in flight was triggered. Captured from
   * NavigationStart because NavigationEnd does not carry it and
   * `getCurrentNavigation()` is already null by then. Only refires read it —
   * the entry fire resolves its own trigger from the captured entry navigation,
   * which is why this is `undefined` for the entry (AdminPage does not exist
   * yet when the entry navigation starts).
   */
  private navigationTrigger: NavigationStart['navigationTrigger'];

  constructor() {
    // Non-null in production: AdminPage is constructed during route activation,
    // while the navigation is still current. Null after NavigationEnd — capture now.
    const entryNavigation = this.router.getCurrentNavigation();
    this.router.events
      .pipe(
        filter(
          (e): e is NavigationStart | NavigationEnd =>
            e instanceof NavigationStart || e instanceof NavigationEnd,
        ),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.navigationTrigger = event.navigationTrigger;
          return;
        }
        const section = resolveAdminSection(
          this.activatedRoute.firstChild?.snapshot.routeConfig?.path,
        );
        if (!section || section === this.reportedSection) {
          return;
        }
        // The shell outlives every child navigation, so the first event this
        // instance sends is the /admin entry and every later one is an in-admin
        // section change — which is why the two resolve their source from
        // different signals: the entry from the navigation that created this
        // instance, a refire from the trigger of the navigation that just ended.
        const isEntry = this.reportedSection === undefined;
        this.reportedSection = section;
        this.analyticsService
          .sendAdminSectionViewedEvent({
            section,
            source: isEntry
              ? resolveAdminEntrySource(entryNavigation)
              : resolveAdminRefireSource(this.navigationTrigger),
            // Only the router's default redirect picks a section on the user's
            // behalf, and it can only do so on entry.
            isDefaultSection: isEntry && isDefaultAdminEntry(event.url),
          })
          .subscribe();
      });
  }
}
